"use client";

import React, { useEffect, useState } from "react";
import { Typography } from "@mui/material";

export default function PostersList() {
  return (
    <div
      style={{
        marginTop: "50px",
        padding: "20px",
        borderRadius: "10px",
        background: "#fff",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          color: "#c62828",
          fontWeight: "bold",
          fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
          textAlign: "center",
        }}
      >
        Approved Posters
      </Typography>

      <PostersTable />
    </div>
  );
}

const PostersTable = () => {
  const [conferences, setConferences] = useState([]);
  const [selectedConferenceId, setSelectedConferenceId] = useState("");
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingConferences, setLoadingConferences] = useState(true);
  const [error, setError] = useState(null);
  const [conferenceError, setConferenceError] = useState(null);

  const CONFERENCES_API = "https://eventscons.com/eventbackendnew/api/conferences/all";
  const PAPERS_BASE_API = "https://eventscons.com/eventbackendnew/api/papers/con/";

  // Fetch conferences list
  const fetchConferences = async () => {
    try {
      setLoadingConferences(true);
      setConferenceError(null);

      const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

      const res = await fetch(CONFERENCES_API, {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }

      const text = await res.text();
      let json;
      try {
        json = JSON.parse(text);
      } catch (parseError) {
        console.error("JSON parse error:", parseError);
        setConferenceError("Invalid response format from conferences API");
        return;
      }

      // Try different possible structures
      let allConferences =
        json?.conferences ||
        json?.data?.conferences ||
        json?.conferences?.data ||
        json?.data ||
        (Array.isArray(json) ? json : []);

      setConferences(Array.isArray(allConferences) ? allConferences : []);
    } catch (error) {
      console.error("Conferences Fetch Error:", error);
      setConferenceError(`Failed to fetch conferences: ${error.message}`);
    } finally {
      setLoadingConferences(false);
    }
  };

  // Fetch papers for selected conference
  const fetchPapers = async (conferenceId) => {
    if (!conferenceId) {
      setPapers([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

      const res = await fetch(`${PAPERS_BASE_API}${conferenceId}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }

      const text = await res.text();
      let json;
      try {
        json = JSON.parse(text);
      } catch (parseError) {
        console.error("JSON parse error:", parseError);
        setError("Invalid response format from server");
        return;
      }

      let allPapers =
        json?.papers ||
        json?.data?.papers ||
        json?.papers?.data ||
        json?.data ||
        [];

      const postersOnly = Array.isArray(allPapers)
        ? allPapers.filter((paper) => paper.type === "poster")
        : [];

      setPapers(postersOnly);
    } catch (error) {
      console.error("API Fetch Error:", error);
      setError(`No data found for this conference`);
    } finally {
      setLoading(false);
    }
  };

  // Initial conferences load
  useEffect(() => {
    fetchConferences();
  }, []);

  // When conference selection changes
  useEffect(() => {
    if (selectedConferenceId) {
      fetchPapers(selectedConferenceId);
    } else {
      setPapers([]);
    }
  }, [selectedConferenceId]);

  const handleConferenceChange = (e) => {
    setSelectedConferenceId(e.target.value);
  };

  // Styles
  const th = {
    padding: "12px",
    border: "1px solid #ddd",
    textAlign: "center",
    fontWeight: "bold",
  };

  const td = {
    padding: "10px",
    border: "1px solid #ddd",
    textAlign: "center",
  };

  const tr = {
    background: "#fafafa",
  };

  return (
    <div style={{ marginTop: "20px" }}>
      {/* Conference Dropdown */}
      <div style={{ marginBottom: "20px" }}>
        <label
          htmlFor="conference-select"
          style={{
            display: "block",
            marginBottom: "8px",
            fontWeight: "bold",
            color: "#333",
          }}
        >
          Select Conference:
        </label>
        {loadingConferences ? (
          <p>Loading conferences...</p>
        ) : conferenceError ? (
          <p style={{ color: "#d32f2f" }}>{conferenceError}</p>
        ) : (
          <select
            id="conference-select"
            value={selectedConferenceId}
            onChange={handleConferenceChange}
            style={{
              width: "100%",
              maxWidth: "500px",
              padding: "10px",
              fontSize: "16px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              background: "#fff",
            }}
          >
            <option value="">-- Choose a conference --</option>
            {conferences.map((conf) => (
              <option key={conf.id} value={conf.id}>
                {conf.title || conf.name || `Conference ${conf.id}`}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Papers Table */}
      {!selectedConferenceId ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
          <p>Please select a conference to view posters.</p>
        </div>
      ) : loading ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <p>Loading posters...</p>
        </div>
      ) : error ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#d32f2f" }}>
          <p>{error}</p>
          <button
            onClick={() => fetchPapers(selectedConferenceId)}
            style={{
              marginTop: "10px",
              padding: "8px 16px",
              background: "#c62828",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Refresh
          </button>
        </div>
      ) : papers.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <p>No Posters Found for this conference</p>
        </div>
      ) : (
        <div>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              background: "#fff",
              boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            }}
          >
            <thead>
              <tr style={{ background: "#c62828", color: "white" }}>
                <th style={th}>#</th>
                <th style={th}>Title</th>
                <th style={th}>Abstract</th>
                <th style={th}>File</th>
                <th style={th}>Status</th>
              </tr>
            </thead>

            <tbody>
              {papers.map((item, index) => (
                <tr key={item.id || index} style={tr}>
                  <td style={td}>{index + 1}</td>
                  <td
                    style={{
                      ...td,
                      textAlign: "left",
                      maxWidth: "200px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {item.title || "N/A"}
                  </td>
                  <td
                    style={{
                      ...td,
                      textAlign: "left",
                      maxWidth: "250px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {item.abstract || "No abstract"}
                  </td>
                  <td style={td}>
                    {item.file_path ? (
                      <a
                        href={`https://eventscons.com/eventbackendnew/storage/app/public/${item.file_path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          padding: "6px 10px",
                          background: "#1976d2",
                          color: "#fff",
                          borderRadius: "4px",
                          textDecoration: "none",
                          fontSize: "12px",
                        }}
                      >
                        View File
                      </a>
                    ) : (
                      "No File"
                    )}
                  </td>
                  <td style={td}>
                    <span
                      style={{
                        padding: "4px 8px",
                        borderRadius: "12px",
                        fontSize: "12px",
                        fontWeight: "bold",
                        background:
                          item.status === "approved" ? "#4caf50" : "#ff9800",
                        color: "white",
                      }}
                    >
                      {item.status || "pending"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};